// @core/stores/project/ProjectEpicStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  AddItemToEpicRequest,
  CreateEpicRequest,
  Epic,
  ItemResponse,
  UpdateEpicRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectEpicStore {
  // List state
  items: Epic[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Epic | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Epic>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Epic | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Epic | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add item to epic
   */
  async AddItemToEpic(project_id: string, epic_id: string, data: AddItemToEpicRequest): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.AddItemToEpic(project_id, epic_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create epic
   */
  async CreateEpic(project_id: string, data: CreateEpicRequest): Promise<Epic> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.CreateEpic(project_id, data);
      const item = response.epic;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete epic
   */
  async DeleteEpic(project_id: string, epic_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await projectApi.DeleteEpic(project_id, epic_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== epic_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === epic_id) {
          this.currentItem = null;
        }

        this.removeFromCache(epic_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get epic
   */
  async GetEpic(project_id: string, epic_id: string): Promise<Epic> {
    // Check cache first
    const cached = this.cache.get(epic_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetEpic(project_id, epic_id);
      const item = response.epic;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List epics
   */
  async ListEpics(project_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListEpics(project_id, params);

      runInAction(() => {
        this.items = response.epics?.epics ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove item from epic
   */
  async RemoveItemFromEpic(project_id: string, epic_id: string, item_id: string): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.RemoveItemFromEpic(project_id, epic_id, item_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update epic
   */
  async UpdateEpic(project_id: string, epic_id: string, data: UpdateEpicRequest): Promise<Epic> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateEpic(project_id, epic_id, data);
      const item = response.epic;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const projectEpicStore = new ProjectEpicStore();
